// ============================================================
// PHOTO LINKS — private storage, expiring links
// ============================================================
// Photos in the 'inspection-photos' bucket are shown through signed links
// that expire, so they can't be opened without signing in. Records store
// either the old permanent public link or a file path; both work here.

import { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'

const BUCKET = 'inspection-photos'
const LINK_SECONDS = 60 * 60
const REUSE_MARGIN_MS = 10 * 60 * 1000 // re-sign when less than 10 minutes remain

const cache = new Map() // path -> { url, expiresAt }

// File path inside the bucket, or null for anything that isn't a stored photo
// (blob:/data: previews, other websites).
export function photoPath(value) {
  if (!value || typeof value !== 'string') return null
  if (value.startsWith('blob:') || value.startsWith('data:')) return null
  const m = value.match(new RegExp(`/storage/v1/object/(?:public|sign|authenticated)/${BUCKET}/([^?#]+)`))
  if (m) return decodeURIComponent(m[1])
  if (/^https?:\/\//i.test(value)) return null
  return value.replace(/^\/+/, '')
}

// Signed links for a list of stored photos, in the same order.
// Values that aren't stored photos are returned unchanged; a photo that
// can't be signed comes back as null.
export async function signPhotoUrls(values) {
  const list = Array.isArray(values) ? values : []
  const now = Date.now()
  const needed = [...new Set(list.map(photoPath).filter(p => {
    if (!p) return false
    const hit = cache.get(p)
    return !hit || hit.expiresAt - now < REUSE_MARGIN_MS
  }))]
  if (needed.length) {
    try {
      const { data, error } = (await supabase.storage.from(BUCKET).createSignedUrls(needed, LINK_SECONDS)) || {}
      if (error) console.error('Could not create photo links:', error.message)
      for (const item of data || []) {
        if (item?.signedUrl) cache.set(item.path, { url: item.signedUrl, expiresAt: now + LINK_SECONDS * 1000 })
      }
    } catch (err) {
      console.error('Could not create photo links:', err)
    }
  }
  return list.map(v => {
    const p = photoPath(v)
    if (!p) return v
    return cache.get(p)?.url || null
  })
}

export async function signPhotoUrl(value) {
  const [url] = await signPhotoUrls([value])
  return url
}

// React hook: signed links for a list of stored photos (null until ready).
export function useSignedPhotoUrls(values) {
  const key = JSON.stringify(values || [])
  const [urls, setUrls] = useState(() => (values || []).map(v => (photoPath(v) ? null : v)))
  useEffect(() => {
    let cancelled = false
    const list = JSON.parse(key)
    setUrls(list.map(v => (photoPath(v) ? cache.get(photoPath(v))?.url || null : v)))
    signPhotoUrls(list).then(signed => { if (!cancelled) setUrls(signed) })
    return () => { cancelled = true }
  }, [key])
  return urls
}

export function useSignedPhotoUrl(value) {
  return useSignedPhotoUrls(value ? [value] : [])[0] || null
}

// Drop-in replacement for <img> when src is a stored photo.
export function SignedImg({ src, alt = '', ...props }) {
  const url = useSignedPhotoUrl(src)
  if (!url) return <span aria-label={alt} style={{ ...props.style, display: 'inline-block', background: 'rgba(128,128,128,0.15)' }} />
  return <img src={url} alt={alt} {...props} />
}

// Thumbnail that opens the full photo in a new tab.
export function SignedPhotoLink({ src, alt = '', style }) {
  const url = useSignedPhotoUrl(src)
  if (!url) return <span aria-label={alt} style={{ ...style, display: 'inline-block', background: 'rgba(128,128,128,0.15)' }} />
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={alt} style={style} /></a>
}
